/**
 * Build a stable-ish CSS selector path for an element — useful to pinpoint
 * exactly which node an auto-tracked interaction happened on.
 *
 * Rules (kept deliberately simple + cheap):
 * - An `id` short-circuits: `#<id>` (ids are meant to be unique).
 * - Otherwise walk up to 6 ancestors; each segment is the lowercased tag name,
 *   plus `:nth-of-type(n)` when the element has same-tag siblings. If an
 *   ancestor has an id, anchor there (`tag#id`) and stop.
 * - Ids are escaped with `CSS.escape` when available.
 */

const MAX_DEPTH = 6
/**
 * Cap on sibling scanning per level. `:nth-of-type` needs to walk siblings to
 * find the index; in a container with thousands of children that could be
 * O(n) work on a click. Bounding it keeps every call O(1)-ish (≤ MAX_DEPTH ×
 * MAX_SIBLING_SCAN) at the cost of dropping the index for pathologically large
 * lists (the segment falls back to a bare tag — still a valid selector).
 */
const MAX_SIBLING_SCAN = 100

/** Escape an id for use in a selector (CSS.escape when available). */
export function escapeCssIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, '\\$&')
}

/**
 * `:nth-of-type` index (1-based) — or 0 when the element has no same-tag
 * sibling OR when scanning exceeds {@link MAX_SIBLING_SCAN} (bail out and omit
 * the index to keep the work bounded on huge sibling lists).
 */
function nthOfType(el: Element): number {
  let index = 1
  let hasSibling = false
  let scanned = 0
  let prev = el.previousElementSibling
  while (prev) {
    if (++scanned > MAX_SIBLING_SCAN) return 0
    if (prev.tagName === el.tagName) {
      index++
      hasSibling = true
    }
    prev = prev.previousElementSibling
  }
  let next = el.nextElementSibling
  while (!hasSibling && next) {
    if (++scanned > MAX_SIBLING_SCAN) return 0
    if (next.tagName === el.tagName) hasSibling = true
    next = next.nextElementSibling
  }
  return hasSibling ? index : 0
}

/**
 * Compute a CSS selector path for `el` (empty string for non-elements).
 */
export function getCssSelector(el: Element | null | undefined): string {
  if (!el || el.nodeType !== 1) return ''
  if (el.id) return `#${escapeCssIdent(el.id)}`

  const parts: string[] = []
  let node: Element | null = el
  while (node && node.nodeType === 1 && parts.length < MAX_DEPTH) {
    let segment = node.tagName.toLowerCase()
    if (node.id) {
      parts.unshift(`${segment}#${escapeCssIdent(node.id)}`)
      break
    }
    const nth = nthOfType(node)
    if (nth) segment += `:nth-of-type(${nth})`
    parts.unshift(segment)
    node = node.parentElement
  }
  return parts.join(' > ')
}
